import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { canvasesApi } from '@/api/endpoints/canvases';
import { isApiError } from '@/api/errors';
import type { CanvasConnection, CanvasDocument, CanvasNode } from '@/api/types';
import { httpCanvasSync, type CanvasSync } from './sync';

export const canvasDocumentKey = (id: number) => ['canvas-document', id] as const;

export function useCanvasDocument(id: number | null, sync: CanvasSync = httpCanvasSync) {
  return useQuery({
    queryKey: canvasDocumentKey(id ?? 0),
    enabled: id !== null,
    queryFn: () => sync.load(id!),
    staleTime: Infinity,
    gcTime: 0,
  });
}

/**
 * Предзагрузка документа при наведении на карточку канваса: открытие становится
 * мгновенным, а перенос локальной раскладки (S8a) успевает пройти заранее.
 */
export function usePrefetchCanvasDocument(sync: CanvasSync = httpCanvasSync) {
  const queryClient = useQueryClient();
  return useCallback(
    (id: number) => {
      void queryClient.prefetchQuery({
        queryKey: canvasDocumentKey(id),
        queryFn: () => sync.load(id),
        staleTime: 15_000,
        gcTime: 60_000,
      });
    },
    [queryClient, sync],
  );
}

export function useSharedCanvas(token: string | null) {
  return useQuery({
    queryKey: ['shared-canvas', token],
    enabled: !!token,
    queryFn: () => canvasesApi.shared(token!),
    retry: false,
    staleTime: Infinity,
  });
}

export type SaveStatus = 'saved' | 'saving' | 'offline';

const AUTOSAVE_DELAY_MS = 900;

/**
 * Автосохранение (CNV-17): каждое изменение документа планирует запись через
 * 900 мс, индикатор «Saving… / Saved / Offline»; при уходе со страницы
 * незаписанное досылается сразу. Ответ 409 (`version_conflict`) значит, что
 * документ записали в другом месте: сервер присылает актуальный документ,
 * он подменяет локальный через `onConflict`, несохранённые правки уходят.
 */
export function useAutosave(options: {
  canvasId: number;
  enabled: boolean;
  revision: number;
  initialVersion: number;
  getPayload: () => { nodes: CanvasNode[]; connections: CanvasConnection[] };
  onConflict?: (document: CanvasDocument) => void;
  sync?: CanvasSync;
}) {
  const {
    canvasId,
    enabled,
    revision,
    initialVersion,
    getPayload,
    onConflict,
    sync = httpCanvasSync,
  } = options;
  const [status, setStatus] = useState<SaveStatus>('saved');
  const versionRef = useRef(initialVersion);
  const pendingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getPayloadRef = useRef(getPayload);
  getPayloadRef.current = getPayload;
  const onConflictRef = useRef(onConflict);
  onConflictRef.current = onConflict;

  const flush = useCallback(async () => {
    if (!pendingRef.current) return;
    pendingRef.current = false;
    setStatus('saving');
    try {
      const result = await sync.save(canvasId, {
        ...getPayloadRef.current(),
        version: versionRef.current,
      });
      versionRef.current = result.version;
      setStatus(pendingRef.current ? 'saving' : 'saved');
    } catch (error) {
      if (isApiError(error) && error.status === 409) {
        try {
          const body = error.data as { version?: number; data?: CanvasDocument } | null;
          const fresh = body?.data ?? (await sync.load(canvasId));
          versionRef.current = body?.version ?? fresh.version;
          pendingRef.current = false;
          setStatus('saved');
          onConflictRef.current?.(fresh);
          return;
        } catch {
          // не смогли перечитать — ниже обычный offline с повтором
        }
      }
      pendingRef.current = true;
      setStatus('offline');
    }
  }, [canvasId, sync]);

  useEffect(() => {
    if (!enabled || revision === 0) return;
    pendingRef.current = true;
    setStatus('saving');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void flush();
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [revision, enabled, flush]);

  // Уход со страницы: незаписанное уходит сразу.
  useEffect(() => {
    if (!enabled) return;
    const onHide = () => {
      if (pendingRef.current) void flush();
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      onHide();
    };
  }, [enabled, flush]);

  return { status, flush };
}
